import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import Image from '@tiptap/extension-image';
import { ResizableImageNodeView } from './ResizableImageNodeView';
import { ResizableImageAttributes, ResizeOptions } from './types';

export interface ResizableImageOptions extends ResizeOptions {
  inline?: boolean;
  allowBase64?: boolean;
  HTMLAttributes?: Record<string, any>;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    resizableImage: {
      /**
       * Insert a resizable image
       */
      setResizableImage: (options: {
        src: string;
        alt?: string;
        title?: string;
        width?: number;
        height?: number;
      }) => ReturnType;
      /**
       * Update resizable image size
       */
      updateResizableImageSize: (options: {
        width: number;
        height: number;
      }) => ReturnType;
    };
  }
}

/**
 * ResizableImage Extension
 * 
 * Extends the standard TipTap Image extension with drag-to-resize functionality.
 * Uses a custom NodeView to render images with resize handles and maintains
 * aspect ratio during resizing operations.
 */
export const ResizableImage = Node.create<ResizableImageOptions>({
  name: 'resizableImage',

  addOptions() {
    return {
      inline: false,
      allowBase64: true,
      maintainAspectRatio: true,
      minWidth: 50,
      maxWidth: 1200,
      minHeight: 50,
      maxHeight: 800,
      handleSize: 8,
      borderWidth: 2,
      HTMLAttributes: {},
    };
  },

  inline() {
    return this.options.inline;
  },

  group() {
    return this.options.inline ? 'inline' : 'block';
  },

  draggable: false, // ✅ Disabled TipTap dragging - using custom position dragging instead
  
  // Make the node selectable - CRITICAL for NodeView selection
  selectable: true,
  
  // Mark as atom node to ensure proper selection behavior
  atom: true,

  addAttributes() {
    return {
      src: {
        default: null,
        parseHTML: element => element.getAttribute('src'),
        renderHTML: attributes => {
          if (!attributes.src) {
            return {};
          }
          return { src: attributes.src };
        },
      },
      alt: {
        default: null,
        parseHTML: element => element.getAttribute('alt'),
        renderHTML: attributes => {
          if (!attributes.alt) {
            return {};
          }
          return { alt: attributes.alt };
        },
      },
      title: {
        default: null,
        parseHTML: element => element.getAttribute('title'),
        renderHTML: attributes => {
          if (!attributes.title) {
            return {};
          }
          return { title: attributes.title };
        },
      },
      width: {
        default: null,
        parseHTML: element => {
          const width = element.getAttribute('width') || element.style.width;
          return width ? parseInt(width, 10) : null;
        },
        renderHTML: attributes => {
          if (!attributes.width) {
            return {};
          }
          return { 
            width: attributes.width,
            style: `width: ${attributes.width}px`,
          };
        },
      },
      height: {
        default: null,
        parseHTML: element => {
          const height = element.getAttribute('height') || element.style.height;
          return height ? parseInt(height, 10) : null;
        },
        renderHTML: attributes => {
          if (!attributes.height) {
            return {};
          }
          return { 
            height: attributes.height,
            style: `height: ${attributes.height}px`,
          };
        },
      },
      hasBeenResized: {
        default: false,
        parseHTML: element => {
          // Check if element has been manually resized (has explicit dimensions)
          const hasWidth = element.getAttribute('width') || element.style.width;
          const hasHeight = element.getAttribute('height') || element.style.height;
          return !!(hasWidth && hasHeight);
        },
        renderHTML: attributes => {
          // Don't render this attribute to HTML
          return {};
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: this.options.allowBase64
          ? 'img[src]'
          : 'img[src]:not([src^="data:"])',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'img',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        class: 'resizable-image-element',
      }),
    ];
  },

  addCommands() {
    return {
      setResizableImage:
        options =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: options,
          });
        },
      updateResizableImageSize:
        options =>
        ({ tr, state }) => {
          const { selection } = state;
          const { from } = selection;
          
          tr.setNodeMarkup(from, undefined, {
            ...state.doc.nodeAt(from)?.attrs,
            width: options.width,
            height: options.height,
          });
          
          return true;
        },
    };
  },

  addNodeView() {
    return ReactNodeViewRenderer(ResizableImageNodeView, {
      className: 'resizable-image-wrapper',
      as: 'div',
      contentDOMElementTag: 'div',
    });
  },

  onCreate() {
    console.log('🎯 [ResizableImage] Extension created and initialized');
  },

  onSelectionUpdate() {
    console.log('🎯 [ResizableImage] Selection updated in extension');
  },
  
  onUpdate() {
    console.log('🎯 [ResizableImage] Extension updated');
  },
}); 